// Клиентский error reporter. Перехватывает window.error, unhandledrejection,
// предоставляет captureException(err, ctx) и ErrorBoundary.
// Шлёт batch'ем на /api/_errors/ingest каждые 5 секунд или сразу если 5+ накопились.

interface QueuedError {
  message: string;
  stack?: string;
  level: 'error' | 'warn' | 'fatal';
  url: string;
  userAgent: string;
  context?: unknown;
  ts: string;
  sessionId: string;
}

const SESSION_KEY = 'mykanban.session_id';
const OFFLINE_QUEUE_KEY = 'mykanban.errorReporter.offline';
const BATCH_SIZE = 5;
const FLUSH_INTERVAL_MS = 5000;
const MAX_QUEUE = 100;
const OFFLINE_MAX = 200;

function loadOfflineQueue(): QueuedError[] {
  try {
    const raw = localStorage.getItem(OFFLINE_QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}
function saveOfflineQueue(items: QueuedError[]) {
  try {
    const trimmed = items.slice(-OFFLINE_MAX);
    localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(trimmed));
  } catch { /* localStorage full or disabled */ }
}
function appendOffline(items: QueuedError[]) {
  if (items.length === 0) return;
  saveOfflineQueue([...loadOfflineQueue(), ...items]);
}
function clearOfflineUpTo(count: number) {
  const remaining = loadOfflineQueue().slice(count);
  saveOfflineQueue(remaining);
}

const queue: QueuedError[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let isInitialized = false;

function getSessionId(): string {
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

/**
 * Sentry-style breadcrumb (research §3.2): последовательность действий
 * ведущих к ошибке. Прикрепляется к каждому event при flush — даёт 95%
 * информации для отладки UX-багов без separate session log.
 */
interface Breadcrumb {
  ts: string;
  type: 'navigation' | 'click' | 'fetch' | 'console' | 'custom' | 'log';
  category?: string;
  message: string;
  data?: Record<string, unknown>;
  level?: 'info' | 'warn' | 'error' | 'debug';
}
const BREADCRUMB_LIMIT = 100;
const breadcrumbs: Breadcrumb[] = [];

export function addBreadcrumb(b: Omit<Breadcrumb, 'ts'>) {
  breadcrumbs.push({ ...b, ts: new Date().toISOString() });
  if (breadcrumbs.length > BREADCRUMB_LIMIT) breadcrumbs.shift();
}

function snapshotBreadcrumbs(): Breadcrumb[] {
  return breadcrumbs.slice();
}

function safeStringify(v: unknown): string {
  try { return JSON.stringify(v); } catch { return String(v); }
}

function enqueue(entry: Omit<QueuedError, 'url' | 'userAgent' | 'ts' | 'sessionId'>) {
  if (queue.length >= MAX_QUEUE) return;
  // Snapshot breadcrumbs прикрепляется к context для отладки.
  const ctxWithBc = {
    ...(typeof entry.context === 'object' && entry.context !== null ? entry.context : { _val: entry.context }),
    breadcrumbs: snapshotBreadcrumbs(),
  };
  queue.push({
    ...entry,
    context: ctxWithBc,
    url: window.location.href,
    userAgent: navigator.userAgent,
    ts: new Date().toISOString(),
    sessionId: getSessionId(),
  });
  scheduleFlush();
}

function scheduleFlush() {
  if (queue.length >= BATCH_SIZE) {
    flush();
    return;
  }
  if (flushTimer) return;
  flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS);
}

async function flush() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  // Offline queue priority — сначала старые накопленные, потом текущие.
  const offline = loadOfflineQueue();
  if (offline.length > 0) {
    const offlineBatch = offline.slice(0, BATCH_SIZE);
    try {
      const res = await fetch('/api/_errors/ingest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ errors: offlineBatch }),
        keepalive: true,
      });
      if (res.ok) {
        clearOfflineUpTo(offlineBatch.length);
      } else {
        flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS * 2);
        return;
      }
    } catch {
      flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS * 2);
      return;
    }
  }

  if (queue.length === 0) return;
  const batch = queue.splice(0, BATCH_SIZE);
  try {
    const res = await fetch('/api/_errors/ingest', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ errors: batch }),
      keepalive: true,
    });
    if (!res.ok) {
      queue.unshift(...batch);
      appendOffline(batch);
      flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS * 2);
    }
  } catch {
    queue.unshift(...batch);
    appendOffline(batch);
    flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS * 2);
  }
}

export function initErrorReporter() {
  if (isInitialized) return;
  isInitialized = true;

  window.addEventListener('error', (e) => {
    enqueue({
      message: e.message || 'Unknown error',
      stack: e.error?.stack,
      level: 'error',
      context: {
        filename: e.filename,
        lineno: e.lineno,
        colno: e.colno,
      },
    });
  });

  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason;
    const message = reason instanceof Error ? reason.message : String(reason);
    const stack = reason instanceof Error ? reason.stack : undefined;
    enqueue({
      message: `Unhandled rejection: ${message}`,
      stack,
      level: 'error',
    });
  });

  // Auto-instrumentation breadcrumbs (research §3.2 + Sentry SDK pattern).
  // Click target descriptor.
  window.addEventListener('click', (e) => {
    const t = e.target as HTMLElement | null;
    if (!t) return;
    const tag = t.tagName?.toLowerCase() ?? 'unknown';
    const label = t.getAttribute?.('aria-label') || t.getAttribute?.('title')
      || (t.textContent?.trim().slice(0, 60) ?? '');
    addBreadcrumb({
      type: 'click',
      category: 'ui.click',
      message: `click ${tag}${label ? ` "${label}"` : ''}`,
      data: { tag, id: t.id || undefined, class: t.className || undefined },
    });
  }, { capture: true, passive: true });

  // Navigation
  window.addEventListener('popstate', () => {
    addBreadcrumb({
      type: 'navigation',
      category: 'navigation',
      message: `navigate ${window.location.pathname}`,
      data: { url: window.location.href },
    });
  });
  const origPush = history.pushState;
  history.pushState = function (...args: Parameters<typeof origPush>) {
    addBreadcrumb({
      type: 'navigation',
      category: 'navigation',
      message: `pushState ${args[2] ?? '?'}`,
      data: { url: String(args[2] ?? '') },
    });
    return origPush.apply(this, args);
  };

  // Fetch interceptor: + X-Trace-Id propagation (research §3.4) + breadcrumb.
  const origFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const startedAt = Date.now();
    const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
    const method = init?.method || 'GET';
    const isObs = url.includes('/api/_errors/') || url.includes('/api/_actions/');

    let patchedInit = init;
    if (!isObs) {
      const traceId = crypto.randomUUID();
      const sessionId = getSessionId();
      const headers = new Headers(init?.headers);
      if (!headers.has('X-Trace-Id')) headers.set('X-Trace-Id', traceId);
      if (!headers.has('X-Session-Id')) headers.set('X-Session-Id', sessionId);
      patchedInit = { ...init, headers };
    }

    try {
      const res = await origFetch(input, patchedInit);
      if (!isObs) {
        addBreadcrumb({
          type: 'fetch',
          category: 'http',
          message: `${method} ${url} ${res.status}`,
          level: res.ok ? 'info' : (res.status >= 500 ? 'error' : 'warn'),
          data: {
            url, method, status: res.status, durationMs: Date.now() - startedAt,
            traceId: (patchedInit?.headers as Headers | undefined)?.get('X-Trace-Id') ?? undefined,
          },
        });
      }
      return res;
    } catch (err) {
      if (!isObs) {
        addBreadcrumb({
          type: 'fetch',
          category: 'http',
          message: `${method} ${url} FAILED`,
          level: 'error',
          data: { url, method, error: err instanceof Error ? err.message : String(err), durationMs: Date.now() - startedAt },
        });
      }
      throw err;
    }
  };

  // Online recovery — после reconnect сразу пытаемся выгрузить offline queue.
  window.addEventListener('online', () => { void flush(); });
  if (loadOfflineQueue().length > 0) scheduleFlush();

  // Console.error/warn → breadcrumb
  const origConsoleError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    addBreadcrumb({
      type: 'console',
      category: 'console',
      level: 'error',
      message: args.map(a => typeof a === 'string' ? a : safeStringify(a)).join(' ').slice(0, 300),
    });
    origConsoleError(...args);
  };
  const origConsoleWarn = console.warn.bind(console);
  console.warn = (...args: unknown[]) => {
    addBreadcrumb({
      type: 'console',
      category: 'console',
      level: 'warn',
      message: args.map(a => typeof a === 'string' ? a : safeStringify(a)).join(' ').slice(0, 300),
    });
    origConsoleWarn(...args);
  };

  // Flush на закрытии вкладки
  window.addEventListener('beforeunload', () => {
    if (queue.length > 0) {
      const batch = queue.splice(0, BATCH_SIZE);
      try {
        navigator.sendBeacon(
          '/api/_errors/ingest',
          new Blob([JSON.stringify({ errors: batch })], { type: 'application/json' }),
        );
      } catch {
        /* ignore */
      }
    }
  });
}

export function captureException(err: unknown, context?: unknown) {
  const message = err instanceof Error ? err.message : String(err);
  const stack = err instanceof Error ? err.stack : undefined;
  enqueue({ message, stack, level: 'error', context });
}

export function captureMessage(message: string, level: 'warn' | 'fatal' | 'error' = 'warn', context?: unknown) {
  enqueue({ message, level, context });
}
